import { useEffect, useRef, ReactNode } from "react";
import { Switch, Route, useLocation } from "wouter";
import React from "react";
import { queryClient, setAttributionCtxProvider } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserProfileProvider } from "@/hooks/useUserProfile";
import { useReferralCapture } from "@/hooks/useReferralCapture";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useAttribution } from "@/hooks/useAttribution";
import RequirePermission from "@/components/RequirePermission";
import FeedbackWidget from "@/components/FeedbackWidget";
import { RoleType } from "@shared/permissions";
import Home from "@/pages/Home";
import Rewards from "@/pages/Rewards";
import Redeem from "@/pages/Redeem";
import RedemptionHistory from "@/pages/RedemptionHistory";
import Leaderboard from "@/pages/Leaderboard";
import Referrals from "@/pages/Referrals";
import Restaurant from "@/pages/Restaurant";
import RestaurantList from "@/pages/RestaurantList";
import RestaurantMapView from "@/pages/RestaurantMapView";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import FriendProfile from "@/pages/FriendProfile";
import Profile from "@/pages/Profile";
import SettingsPersonal from "@/pages/SettingsPersonal";
import SettingsPreferences from "@/pages/SettingsPreferences";
import MyReviews from "@/pages/MyReviews";
import FriendsActivity from "@/components/FriendsActivity";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import TestAuth from "@/pages/TestAuth";
import TestCheckoutFlow from "@/pages/TestCheckoutFlow";
import NotFound from "@/pages/not-found";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import DataDeletion from "@/pages/DataDeletion";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminReports from "@/pages/admin/AdminReports";
import AdminComments from "@/pages/admin/AdminComments";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminTags from "@/pages/admin/AdminTags";
import AdminGamification from "@/pages/admin/AdminGamification";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminRestaurants from "@/pages/admin/AdminRestaurants";
import AdminRestaurantManage from "@/pages/admin/AdminRestaurantManage";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminStripeConnect from "@/pages/admin/AdminStripeConnect";
import AdminConfig from "@/pages/admin/AdminConfig";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminTrustedDomains from "@/pages/admin/AdminTrustedDomains";
import AdminKudozProbe from "@/pages/admin/AdminKudozProbe";
import AdminErrorLog from "@/pages/admin/AdminErrorLog";
import AdminReferralNetwork from "@/pages/admin/AdminReferralNetwork";
import PartnerLogin from "@/pages/partner/PartnerLogin";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerReviews from "@/pages/partner/PartnerReviews";
import PartnerPosts from "@/pages/partner/PartnerPosts";
import PartnerKudoz from "@/pages/partner/PartnerKudoz";
import KudozDebug from "@/pages/KudozDebug";
import DebugLogs from "@/pages/DebugLogs";
import PartnerPromotions from "@/pages/partner/PartnerPromotions";
import PartnerAnalytics from "@/pages/partner/PartnerAnalytics";
import PartnerInfluencers from "@/pages/partner/PartnerInfluencers";
import PartnerSettings from "@/pages/partner/PartnerSettings";
import PartnerMenu from "@/pages/partner/PartnerMenu";
import PartnerCoupons from "@/pages/partner/PartnerCoupons";
import PartnerCampaigns from "@/pages/partner/PartnerCampaigns";
import PartnerKudozIncentives from "@/pages/partner/PartnerKudozIncentives";
import PartnerWebsite from "@/pages/partner/PartnerWebsite";
import PartnerPost from "@/pages/partner/PartnerPost";
import PartnerLoyalty from "@/pages/partner/PartnerLoyalty";
import PartnerTools from "@/pages/partner/PartnerTools";
import PartnerPerformance from "@/pages/partner/PartnerPerformance";
import PartnerInbox from "@/pages/partner/PartnerInbox";
import GiftCertificates from "@/pages/GiftCertificates";
import PurchaseGiftCertificate from "@/pages/PurchaseGiftCertificate";
import OwnerLanding from "@/pages/owner/OwnerLanding";
import OwnerOnboarding from "@/pages/owner/OwnerOnboarding";
import OwnerSuccess from "@/pages/owner/OwnerSuccess";
import About from "@/pages/About";
import ContactUs from "@/pages/ContactUs";
import FAQ from "@/pages/FAQ";
import CompetitiveReport from "@/pages/CompetitiveReport";
import PublicRestaurantMenu from "@/pages/PublicRestaurantMenu";
import MyEatzy from "@/pages/MyEatzy";
import Deals from "@/pages/Deals";

import { Loader2 } from "lucide-react";
import ConsumerLayout from "@/components/ConsumerLayout";

function InitialPathHandler() {
  const { isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || isLoading) return;
    handled.current = true;

    let targetPath: string | null = null;

    const params = new URLSearchParams(window.location.search);
    const queryPath = params.get('path');
    if (queryPath) {
      targetPath = queryPath;
    }

    if (!targetPath && window.location.hash.startsWith('#/')) {
      targetPath = window.location.hash.slice(1);
    }

    if (
      targetPath &&
      targetPath.startsWith('/') &&
      targetPath !== '/' &&
      targetPath !== window.location.pathname &&
      !/[^a-zA-Z0-9/_\-.]/.test(targetPath)
    ) {
      const url = new URL(window.location.href);
      url.searchParams.delete('path');
      url.hash = '';
      window.history.replaceState(null, '', url.pathname + url.search);
      setLocation(targetPath);
    }
  }, [isLoading, setLocation]);

  return null;
}

// Protected Route Component
function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [location, setLocation] = useLocation();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const returnUrl = encodeURIComponent(location);
      setLocation(`/login?returnUrl=${returnUrl}`);
    }
  }, [isAuthenticated, isLoading, location, setLocation]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return <Component />;
}

// Consumer Protected Route — wraps the page in the ConsumerLayout (sidebar + bottom nav)
function ConsumerProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [location, setLocation] = useLocation();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const returnUrl = encodeURIComponent(location);
      setLocation(`/login?returnUrl=${returnUrl}`);
    }
  }, [isAuthenticated, isLoading, location, setLocation]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <ConsumerLayout>
      <Component />
    </ConsumerLayout>
  );
}

// Browsable Route — accessible by anyone (authenticated or not), wrapped in ConsumerLayout
function BrowsableRoute({ component: Component }: { component: React.ComponentType }) {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <ConsumerLayout>
      <Component />
    </ConsumerLayout>
  );
}

// Public Route Component (redirects to home if already authenticated)
function PublicRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  // Track whether we have rendered the child component at least once.
  // The spinner should only block on the very first auth check (app startup).
  // Once Login/Signup has been shown, never unmount it mid-flight — login/signup
  // operations set isLoading=true briefly, which would otherwise destroy the
  // component and discard its error state before setError can run.
  const hasRenderedRef = React.useRef(false);

  React.useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const params = new URLSearchParams(window.location.search);
      const returnUrl = params.get('returnUrl') || '/';
      setLocation(returnUrl);
    }
  }, [isAuthenticated, isLoading, setLocation]);

  // Show spinner only during the initial app-load auth check, before we know
  // whether the user is authenticated (prevents a flash of the login form).
  if (!hasRenderedRef.current && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Redirect pending — keep showing spinner while useEffect fires the navigation.
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  hasRenderedRef.current = true;
  return <Component />;
}


// Wires referral click-tracking for every page a referral link can land on.
// Must live inside Router so wouter's useSearch() has the correct context.
function ReferralInit() {
  useReferralCapture();
  return null;
}

function Router() {
  return (
    <>
      <ReferralInit />
    <Switch>
      {/* Public routes */}
      <Route path="/login" component={() => <PublicRoute component={Login} />} />
      <Route path="/signup" component={() => <PublicRoute component={Signup} />} />
      <Route path="/forgot-password" component={() => <PublicRoute component={ForgotPassword} />} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/auth/callback" component={AuthCallback} />
      <Route path="/test-auth" component={TestAuth} />
      <Route path="/test-checkout" component={TestCheckoutFlow} />
      
      {/* Consumer routes — wrapped in ConsumerLayout (desktop sidebar + mobile bottom nav) */}
      <Route path="/" component={() => <BrowsableRoute component={Home} />} />
      <Route path="/friends" component={() => <ConsumerProtectedRoute component={FriendsActivity} />} />
      <Route path="/my-eatzy" component={() => <ConsumerProtectedRoute component={MyEatzy} />} />
      <Route path="/deals" component={() => <BrowsableRoute component={Deals} />} />
      <Route path="/rewards" component={() => <ConsumerProtectedRoute component={Rewards} />} />
      <Route path="/redeem" component={() => <ConsumerProtectedRoute component={Redeem} />} />
      <Route path="/redeem/history" component={() => <ConsumerProtectedRoute component={RedemptionHistory} />} />
      <Route path="/leaderboard" component={() => <ConsumerProtectedRoute component={Leaderboard} />} />
      <Route path="/referrals" component={() => <ConsumerProtectedRoute component={Referrals} />} />
      <Route path="/restaurant/:id" component={() => <BrowsableRoute component={Restaurant} />} />
      <Route path="/restaurants" component={() => <BrowsableRoute component={RestaurantList} />} />
      <Route path="/restaurants/map" component={() => <BrowsableRoute component={RestaurantMapView} />} />
      <Route path="/checkout" component={() => <ProtectedRoute component={Checkout} />} />
      <Route path="/order-success/:checkoutId/:clientId" component={() => <ProtectedRoute component={OrderSuccess} />} />
      <Route path="/profile/settings/personal" component={() => <ConsumerProtectedRoute component={SettingsPersonal} />} />
      <Route path="/profile/settings/preferences" component={() => <ConsumerProtectedRoute component={SettingsPreferences} />} />
      <Route path="/profile/gift-certificates" component={() => <ConsumerProtectedRoute component={GiftCertificates} />} />
      <Route path="/purchase-gift-certificate/:clientId" component={() => <ProtectedRoute component={PurchaseGiftCertificate} />} />
      <Route path="/profile/reviews" component={() => <ConsumerProtectedRoute component={MyReviews} />} />
      <Route path="/profile/:userId" component={() => <ConsumerProtectedRoute component={FriendProfile} />} />
      <Route path="/profile" component={() => <ConsumerProtectedRoute component={Profile} />} />
      <Route path="/admin" component={() => <ProtectedRoute component={AdminDashboard} />} />
      <Route path="/admin/restaurants" component={() => <ProtectedRoute component={AdminRestaurants} />} />
      <Route path="/admin/restaurants/:eatzyId/manage" component={() => <ProtectedRoute component={AdminRestaurantManage} />} />
      <Route path="/admin/reviews" component={() => <ProtectedRoute component={AdminReviews} />} />
      <Route path="/admin/reports" component={() => <ProtectedRoute component={AdminReports} />} />
      <Route path="/admin/comments" component={() => <ProtectedRoute component={AdminComments} />} />
      <Route path="/admin/users" component={() => <ProtectedRoute component={AdminUsers} />} />
      <Route path="/admin/tags" component={() => <ProtectedRoute component={AdminTags} />} />
      <Route path="/admin/gamification" component={() => <ProtectedRoute component={AdminGamification} />} />
      <Route path="/admin/settings" component={() => <ProtectedRoute component={AdminSettings} />} />
      <Route path="/admin/coupons" component={() => <ProtectedRoute component={AdminCoupons} />} />
      <Route path="/admin/stripe-connect" component={() => <ProtectedRoute component={AdminStripeConnect} />} />
      <Route path="/admin/config" component={() => <ProtectedRoute component={AdminConfig} />} />
      <Route path="/admin/feedback" component={() => <ProtectedRoute component={AdminFeedback} />} />
      <Route path="/admin/trusted-domains" component={() => <ProtectedRoute component={AdminTrustedDomains} />} />
      <Route path="/admin/kudoz-probe" component={() => <ProtectedRoute component={AdminKudozProbe} />} />
      <Route path="/admin/error-log" component={() => <ProtectedRoute component={AdminErrorLog} />} />
      <Route path="/admin/referral-network" component={() => <ProtectedRoute component={AdminReferralNetwork} />} />
      <Route path="/partner/login" component={PartnerLogin} />
      <Route path="/partner" component={() => <RequirePermission component={PartnerDashboard} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/menu" component={() => <RequirePermission component={PartnerMenu} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/reviews" component={() => <RequirePermission component={PartnerReviews} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/posts" component={() => <RequirePermission component={PartnerPosts} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/kudoz" component={() => <RequirePermission component={PartnerKudoz} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/coupons" component={() => <RequirePermission component={PartnerCoupons} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/campaigns" component={() => <RequirePermission component={PartnerCampaigns} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/kudoz-incentives" component={() => <RequirePermission component={PartnerKudozIncentives} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/promotions" component={() => <RequirePermission component={PartnerPromotions} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/analytics" component={() => <RequirePermission component={PartnerAnalytics} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/influencers" component={() => <RequirePermission component={PartnerInfluencers} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/settings" component={() => <RequirePermission component={PartnerSettings} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/website" component={() => <RequirePermission component={PartnerWebsite} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/post" component={() => <RequirePermission component={PartnerPost} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/loyalty" component={() => <RequirePermission component={PartnerLoyalty} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/performance" component={() => <RequirePermission component={PartnerPerformance} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/tools" component={() => <RequirePermission component={PartnerTools} role={RoleType.RestaurantManager} />} />
      <Route path="/partner/inbox" component={() => <RequirePermission component={PartnerInbox} role={RoleType.RestaurantManager} />} />
      <Route path="/debug/logs" component={() => <ProtectedRoute component={DebugLogs} />} />
      <Route path="/kudoz/debug" component={() => <ProtectedRoute component={KudozDebug} />} />
      
      {/* Public restaurant menu page (deep link from social posts) */}
      <Route path="/r/:eatzyId" component={PublicRestaurantMenu} />

      {/* Owner portal (public landing, onboarding with step 3 auth gate) */}
      <Route path="/for-restaurants" component={OwnerLanding} />
      <Route path="/owner" component={OwnerLanding} />
      <Route path="/owner/onboarding" component={OwnerOnboarding} />
      <Route path="/owner/success" component={OwnerSuccess} />
      
      {/* Top-level Gift Certificates route (accessible from nav menu) */}
      <Route path="/gift-certificates" component={() => <ProtectedRoute component={GiftCertificates} />} />
      
      <Route path="/about" component={About} />
      <Route path="/contact" component={ContactUs} />
      <Route path="/faq" component={() => <ProtectedRoute component={FAQ} />} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/data-deletion" component={DataDeletion} />
      <Route path="/report/competitive" component={CompetitiveReport} />
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function AttributionInit() {
  const { getAttributionCtx } = useAttribution();
  const registered = useRef(false);

  useEffect(() => {
    if (registered.current) return;
    registered.current = true;
    setAttributionCtxProvider(getAttributionCtx);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProfileProvider>
          <TooltipProvider>
            <AttributionInit />
            <Toaster />
            <InitialPathHandler />
            <Router />
            <FeedbackWidget />
          </TooltipProvider>
        </UserProfileProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
